import { useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogBody,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Flex,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Text,
} from '@optiaxiom/react';
import {
  IconCheck,
  IconEllipsisSolid,
  IconPen,
  IconPlus,
  IconTrashCan,
  IconXmark,
} from '@optimizely/axiom-icons';

import { ScopeBundle, ScopeBundleStatus } from '@frontend/types';

import { useDeleteScopeBundle, useReviewScopeBundle } from '../../hooks';

type ScopeBundleTableProps = {
  bundles: ScopeBundle[];
  onAddBundle: () => void;
  onEditBundle: (bundle: ScopeBundle) => void;
};

const getStatusBadge = (status: ScopeBundleStatus) => {
  switch (status) {
    case 'approved':
      return <Badge intent="success">Approved</Badge>;
    case 'rejected':
      return <Badge intent="danger">Rejected</Badge>;
    case 'pending_approval':
    default:
      return <Badge intent="warning">Pending</Badge>;
  }
};

const formatScopes = (scopes: string): string => {
  const scopeList = scopes.split(',').map(s => s.trim());
  if (scopeList.length <= 3) {
    return scopeList.join(', ');
  }
  return `${scopeList.slice(0, 3).join(', ')} +${scopeList.length - 3} more`;
};

export function ScopeBundleTable({ bundles, onAddBundle, onEditBundle }: ScopeBundleTableProps) {
  const { mutate: deleteBundle, isPending: isDeleting } = useDeleteScopeBundle();
  const { reviewBundle, isReviewing } = useReviewScopeBundle();
  const [deletingBundle, setDeletingBundle] = useState<ScopeBundle | null>(null);

  const handleApprove = (bundleId: string) => {
    reviewBundle({ bundleId, payload: { status: 'approved' } });
  };

  const handleReject = (bundleId: string) => {
    reviewBundle({ bundleId, payload: { status: 'rejected' } });
  };

  const handleDelete = () => {
    if (deletingBundle) {
      deleteBundle(deletingBundle.id, {
        onSuccess: () => {
          setDeletingBundle(null);
        },
      });
    }
  };

  if (bundles.length === 0) {
    return (
      <Flex flexDirection="column" gap="12" alignItems="center" p="16">
        <Text color="fg.tertiary">No scope bundles configured</Text>
        <Button appearance="default" onClick={onAddBundle}>
          <IconPlus size={16} />
          Add Scope Bundle
        </Button>
      </Flex>
    );
  }

  return (
    <Flex flexDirection="column" gap="12" w="full">
      <Flex flexDirection="row" justifyContent="space-between" alignItems="center">
        <Text fontWeight="500">Scope Bundles</Text>
        <Button appearance="default" onClick={onAddBundle}>
          <IconPlus size={16} />
          Add Bundle
        </Button>
      </Flex>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHeaderCell>Name</TableHeaderCell>
            <TableHeaderCell>Scopes</TableHeaderCell>
            <TableHeaderCell>Status</TableHeaderCell>
            <TableHeaderCell>Created By</TableHeaderCell>
            <TableHeaderCell w="80">Actions</TableHeaderCell>
          </TableRow>
        </TableHeader>
        <TableBody>
          {bundles.map(bundle => (
            <TableRow key={bundle.id}>
              <TableCell>
                <Flex flexDirection="column">
                  <Text fontWeight="500">{bundle.name}</Text>
                  {bundle.description && (
                    <Text color="fg.tertiary" fontSize="sm">
                      {bundle.description}
                    </Text>
                  )}
                </Flex>
              </TableCell>
              <TableCell>
                <Text fontSize="sm" style={{ fontFamily: 'monospace' }}>
                  {formatScopes(bundle.scopes)}
                </Text>
              </TableCell>
              <TableCell>{getStatusBadge(bundle.status)}</TableCell>
              <TableCell>{bundle.created_by}</TableCell>
              <TableCell>
                <DropdownMenu>
                  <DropdownMenuTrigger
                    appearance="subtle"
                    aria-label="Actions"
                    icon={<IconEllipsisSolid />}
                  />
                  <DropdownMenuContent side="left">
                    <DropdownMenuItem onClick={() => onEditBundle(bundle)}>
                      <IconPen size={16} />
                      Edit
                    </DropdownMenuItem>

                    {bundle.status === 'pending_approval' && (
                      <>
                        <DropdownMenuItem
                          onClick={() => handleApprove(bundle.id)}
                          disabled={isReviewing}
                        >
                          <IconCheck size={16} />
                          Approve
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => handleReject(bundle.id)}
                          disabled={isReviewing}
                        >
                          <IconXmark size={16} />
                          Reject
                        </DropdownMenuItem>
                      </>
                    )}

                    <DropdownMenuItem
                      onClick={() => setDeletingBundle(bundle)}
                      disabled={isDeleting}
                    >
                      <IconTrashCan size={16} />
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <AlertDialog open={!!deletingBundle} onOpenChange={open => !open && setDeletingBundle(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>Delete Scope Bundle?</AlertDialogHeader>
          <AlertDialogBody>
            Are you sure you want to delete &quot;{deletingBundle?.name}&quot;? This action cannot
            be undone.
          </AlertDialogBody>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} disabled={isDeleting}>
              Yes, delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Flex>
  );
}
